import React, { useCallback, useMemo } from 'react'
import { Box, Container, Alert } from '@mui/material'
import { ServicePackageStepper } from '../components/AgencyServicePackageForm/ServicePackageStepper'
import { ServicePackageFormData } from '../types/servicePackage'
import { useAppSelector } from '@store/index'
import { submitServicePackage } from '../services/servicePackageService'
import { useLocation, useParams } from 'react-router-dom'

export const ServicePackageFormAgencyPage: React.FC = () => {
  const location = useLocation()
  const { serviceId } = useParams<{ serviceId: string }>()
  const { user } = useAppSelector(state => state.auth)
  // console.log("user agency: ", user)

  const { id } = useParams()
  const locationState = location.state as {
    initialData?: ServicePackageFormData
    servicePackageId?: string
  } | null

  const isEditMode = Boolean(serviceId)
  const initialData = locationState?.initialData ?? null

  const stepperInitialData = useMemo(() => {
    if (!initialData) return undefined
    return serviceId && !initialData.id
      ? { ...initialData, id: serviceId }
      : initialData
  }, [initialData, serviceId])

  const selectedProviderId = useAppSelector(state => state.app.selectedAgencyProviderId)

  const handleSave = useCallback(
    async (data: ServicePackageFormData) => {
      const therapistId = selectedProviderId || id;
      if (!therapistId) {
        throw new Error('Provider ID is missing. Please select a provider again.')
      }

      try {
        await submitServicePackage(
          { ...data, status: 'draft' as const },
          therapistId
        )
      } catch (error) {
        console.error('Failed to save service package', error)
        throw error
      }
    },
    [selectedProviderId, id]
  )

  const handlePublish = useCallback(
    async (data: ServicePackageFormData) => {
      const therapistId = selectedProviderId || id;
      if (!therapistId) {
        throw new Error('Provider ID is missing. Please select a provider again.')
      }

      try {
        await submitServicePackage(
          { ...data, status: 'published' as const },
          therapistId
        )
      } catch (error) {
        console.error('Failed to publish service package', error)
        throw error
      }
    },
    [selectedProviderId, id]
  )

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {!user?.id && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            You need to be logged in as a provider to create service packages.
          </Alert>
        )}
        {isEditMode && !stepperInitialData && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Unable to load service package details. Please return to the service
            package list and try again.
          </Alert>
        )}
        {isEditMode && !stepperInitialData ? null : (
          <ServicePackageStepper
            initialData={stepperInitialData}
            onSave={handleSave}
            onPublish={handlePublish}
            mode={isEditMode ? 'edit' : 'create'}
            providerId={id}
          />
        )}
      </Container>
    </Box>
  )
}

export default ServicePackageFormAgencyPage
